import L from 'leaflet';
import ReactDOMServer from 'react-dom/server';
import 'leaflet-realtime';

import {
    AnnouncerColors,
    ENDPOINT_DATA_GEOSERVICIOS,
    GIS_ANNOUNCER_ICON_SIZE_PIXELS,
    GIS_SCE_ICON_SIZE_PIXELS,
    //GIS_POINT_ICON_SIZE_PIXELS,
    REALTIME_INTERVAL_SECONDS,
} from '../../../constants';
import AnnouncerIcon from '../icons/AnnouncerIcon';
import AnnouncerTooltip from '../tooltips/AnnouncerTooltip';
import PointIcon from '../icons/SCEIcon';
import { dataGeoServiciosFetch } from '../../../helpers/fetch';
import { invertCoordinates } from '../../../helpers/generalFunctions';

/**
 * @param {number} idCliente - Client id for the request.
 * @param {string} colorPoints - Colors for the points
 * @param {string} ptws - Colors for the points
 * @returns Array of two elements: announcers layergroup and announcers realtime reference, the reference needs to be
 * returned to stop the subscription later.
 */
const getAnnouncersLayerGroupRealtime = (idCliente,colorPoints,ptws) => {
    const arrColors = colorPoints.split(",")
    const announcersLayerGroup = new L.LayerGroup();
    const realtimeReference = L.realtime(
        {
            url: ENDPOINT_DATA_GEOSERVICIOS,
            method: 'POST',
            body: JSON.stringify({
                solicitud: 'Anunciadores',
                idCliente,
                ptws,
            }),
        },
        {
            container: announcersLayerGroup,
            interval: REALTIME_INTERVAL_SECONDS * 1000,
            onEachFeature: ({ properties: { estado, latitud, longitud, nivel, nombre, tipo } }, layer) => {
                layer.bindTooltip(
                    ReactDOMServer.renderToString(
                        <AnnouncerTooltip
                            estado={estado}
                            latitud={latitud}
                            longitud={longitud}
                            nivel={nivel}
                            nombre={nombre}
                            tipo={tipo}

                        />
                    ),
                    { direction: 'top' }
                );
            },
            pointToLayer: (feature, latlng) =>
                L.marker(latlng, {
                    icon: L.divIcon({
                        className: '',
                        html: ReactDOMServer.renderToString(
                            createAnnouncerHtml(feature.properties.estado, feature.properties.nivel, feature.properties.tipo,arrColors)
                        ),
                        iconSize: [GIS_ANNOUNCER_ICON_SIZE_PIXELS, GIS_ANNOUNCER_ICON_SIZE_PIXELS],
                    }),
                    pmIgnore: true,
                }),
            removeMissing: true,
            updateFeature: (feature, oldLayer, newLayer) => newLayer,
        }
    );
    return [announcersLayerGroup, realtimeReference];
};


/**
 * Draw announcersLayerGroup in historic
 * @param {Object} parameters 
  * @returns {L.LayerGroup<any>} group of annuncer historic
 */
const getAnnouncersLayerGroupHistoric = async (parameters) => {

    const pointsGeojson = await dataGeoServiciosFetch(parameters, 'POST');
    const annuncerMarkers = pointsGeojson.features.map((feature) => {
        const { properties: { estado, latitud, longitud, nivel, nombre, tipo } } = feature
        return createAnunccerMarker(invertCoordinates(feature.geometry.coordinates),{estado,nivel,tipo})
            .bindTooltip(ReactDOMServer.renderToString(
                <AnnouncerTooltip
                    estado={estado}
                    latitud={latitud}
                    longitud={longitud}
                    nivel={nivel}
                    nombre={nombre}
                    tipo={tipo}
                />
            ))
    })
    const announcersLayerGroup = new L.LayerGroup(annuncerMarkers);
    return announcersLayerGroup;
};

/**
 * Create marker of annuncer
 * @param {Array} latlng array with coordinates of point
 * @param {Object} feature properties of shape
 * @returns {L.Marker<any>} marker
 */
const createAnunccerMarker = (latlng,{estado,nivel,tipo}) => (
    L.marker(latlng, {
        icon: L.divIcon({
            className: '',
            html: ReactDOMServer.renderToString(
                createAnnouncerHtml(estado,nivel,tipo)
            ),
            iconSize: [GIS_ANNOUNCER_ICON_SIZE_PIXELS, GIS_ANNOUNCER_ICON_SIZE_PIXELS],
        }),
        pmIgnore: true,
    })
)

/**
 * @param {number} estado - Property "estado", the announcer color depends on this property.
 * @param {number} nivel - Property "nivel", the announcer color depends on this property.
 * @param {number} tipo - Property "tipo", the announcer color depends on this property.
 * @returns Announcer React component, ready to be converted to HTML.
 */
const createAnnouncerHtml = (estado, nivel, tipo, arrColors) => {
    if (typeof arrColors !== 'undefined') { 
        if (estado !== 4 && tipo === 1) {
            if (nivel === 3) {
                return <AnnouncerIcon color={arrColors[3]} fontSize={`${GIS_ANNOUNCER_ICON_SIZE_PIXELS}px`} />;
            } else if (nivel === 2) {
                return <AnnouncerIcon color={arrColors[2]} fontSize={`${GIS_ANNOUNCER_ICON_SIZE_PIXELS}px`} />;
            } else if (nivel === 1) {
                return <AnnouncerIcon color={arrColors[1]} fontSize={`${GIS_ANNOUNCER_ICON_SIZE_PIXELS}px`} />;
            } else if (nivel === 0) {
                return <AnnouncerIcon color={arrColors[0]} fontSize={`${GIS_ANNOUNCER_ICON_SIZE_PIXELS}px`} />;
            }
        }
        else if (tipo === 1) {
            
            return <AnnouncerIcon color={AnnouncerColors.DISCONNECTED} fontSize={`${GIS_ANNOUNCER_ICON_SIZE_PIXELS}px`} />;
        }

        if (estado !== 4 && tipo === 2) {
            if (nivel === 3) {
                return (<PointIcon fillColor={arrColors[3]} fontSize={`${GIS_SCE_ICON_SIZE_PIXELS}px`} strokeColor={arrColors[3]} />);
            } else if (nivel === 2) {
                return (<PointIcon fillColor={arrColors[2]} fontSize={`${GIS_SCE_ICON_SIZE_PIXELS}px`} strokeColor={arrColors[2]} />);
            } else if (nivel === 1) {
                return (<PointIcon fillColor={arrColors[1]} fontSize={`${GIS_SCE_ICON_SIZE_PIXELS}px`} strokeColor={arrColors[1]} />);
            } else if (nivel === 0) {
                return (<PointIcon fillColor={arrColors[0]} fontSize={`${GIS_SCE_ICON_SIZE_PIXELS}px`} strokeColor={arrColors[0]} />);
            }
        }
        else if (tipo === 2) {
            return (<PointIcon fillColor={AnnouncerColors.DISCONNECTED} fontSize={`${GIS_SCE_ICON_SIZE_PIXELS}px`} strokeColor={AnnouncerColors.DISCONNECTED} />);
        }
    }
    else { 
        
        if (tipo === 1) {
            return <AnnouncerIcon color={AnnouncerColors.LEVEL_0} fontSize={`${GIS_ANNOUNCER_ICON_SIZE_PIXELS}px`} />;       
        }
        else if (tipo === 2){
            return (<PointIcon fillColor={AnnouncerColors.LEVEL_0} fontSize={`${GIS_SCE_ICON_SIZE_PIXELS}px`} strokeColor={AnnouncerColors.LEVEL_0} />);
        }
 
     }
    };

    export { getAnnouncersLayerGroupRealtime, getAnnouncersLayerGroupHistoric };
